import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Bell,
  Check,
  CheckCheck,
  Loader2,
  MessageSquare,
  Trash2,
  UserPlus,
} from "lucide-react";

import { cn } from "../../lib/utils";
import { useEffectOnce } from "../../hooks/useEffectOnce";
import { useNotifications, type Notification } from "../../hooks/useNotifications";
import { openNotification, resolveNotificationTarget } from "./notificationTarget";
import { Button } from "../ui/button";

// ── Notifications — the extended view ────────────────────────────────────────
// The bell dropdown is a glance: two clamped lines. This page is where a
// notification can actually be read — full title, full message, sender, exact
// time — with unread filtering, mark-all-read, and click-through to wherever
// the notification points.

const typeIcon = (type: string) => {
  switch (type) {
    case "MENTORING_REQUEST":
      return <UserPlus className="h-5 w-5 text-primary" />;
    case "MENTOR_ACCEPTED":
    case "MENTOR_JOINED":
      return <UserPlus className="h-5 w-5 text-emerald-600" />;
    case "NEW_MESSAGE":
    case "PROCESS_CHAT_MESSAGE":
      return <MessageSquare className="h-5 w-5 text-violet-500" />;
    case "ENGAGEMENT_UPDATE":
    case "ENGAGEMENT_COMPLETED":
    case "MILESTONE_COMPLETED":
      return <Check className="h-5 w-5 text-emerald-600" />;
    case "SYSTEM_REMINDER":
    case "SYSTEM_ANNOUNCEMENT":
      return <AlertCircle className="h-5 w-5 text-amber-500" />;
    default:
      return <Bell className="h-5 w-5 text-muted-foreground" />;
  }
};

// Bucket key for grouping the list by day.
const dayKey = (ts: number) => new Date(ts).toISOString().slice(0, 10);

const NotificationsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    hasMore,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const [filter, setFilter] = useState<"all" | "unread">("all");

  useEffectOnce(() => {
    fetchNotifications(false, 0, 50);
  });

  const switchFilter = (f: "all" | "unread") => {
    setFilter(f);
    fetchNotifications(f === "unread", 0, 50);
  };

  const groups = useMemo(() => {
    const byDay = new Map<string, Notification[]>();
    for (const n of notifications) {
      const key = dayKey(n.createdAt);
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key)!.push(n);
    }
    return Array.from(byDay.entries());
  }, [notifications]);

  const dayLabel = (key: string) => {
    const today = dayKey(Date.now());
    const yesterday = dayKey(Date.now() - 24 * 3600 * 1000);
    if (key === today) return t("notif_today");
    if (key === yesterday) return t("notif_yesterday");
    return new Date(key).toLocaleDateString(i18n.language || "en", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
  };

  const open = (n: Notification) => {
    if (!n.isRead) markAsRead(n._id);
    openNotification(n, navigate);
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2.5">
              <Bell className="w-5 h-5 text-primary" />
              {t("notifications")}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {unreadCount > 0
                ? t("notif_unread_count", { count: unreadCount })
                : t("notif_all_caught_up")}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            disabled={unreadCount === 0}
            onClick={markAllAsRead}
          >
            <CheckCheck className="w-4 h-4 me-1.5" />
            {t("mark_all_as_read")}
          </Button>
        </div>

        {/* Filter */}
        <div className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-card p-1 mb-6">
          {(["all", "unread"] as const).map((f) => (
            <button
              key={f}
              onClick={() => switchFilter(f)}
              className={cn(
                "rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer",
                filter === f
                  ? "bg-primary text-primary-foreground font-medium"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {f === "all" ? t("notif_filter_all") : t("notif_filter_unread")}
            </button>
          ))}
        </div>

        {/* List */}
        {isLoading && notifications.length === 0 ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-7 h-7 animate-spin text-muted-foreground" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-200 py-16 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 mb-3 rounded-full bg-muted/60">
              <CheckCheck className="w-7 h-7 text-muted-foreground/60" />
            </div>
            <p className="text-sm font-medium text-foreground">{t("no_notifications_yet")}</p>
            <p className="text-xs text-muted-foreground mt-1">{t("notif_all_caught_up")}</p>
          </div>
        ) : (
          <div className="space-y-7">
            {groups.map(([key, items]) => (
              <section key={key}>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-2.5">
                  {dayLabel(key)}
                </p>
                <div className="space-y-2">
                  {items.map((n: Notification) => {
                    const target = resolveNotificationTarget(n);
                    return (
                      <div
                        key={n._id}
                        onClick={() => open(n)}
                        className={cn(
                          "group rounded-2xl border bg-card p-4 transition-shadow",
                          target ? "cursor-pointer hover:shadow-md" : "cursor-default",
                          !n.isRead ? "border-primary/25 bg-primary/[0.03]" : "border-gray-200"
                        )}
                      >
                        <div className="flex gap-3">
                          <div className="flex-shrink-0 mt-0.5">{typeIcon(n.type)}</div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3">
                              <h3
                                className={cn(
                                  "text-sm text-foreground",
                                  !n.isRead ? "font-semibold" : "font-medium"
                                )}
                              >
                                {n.title}
                                {!n.isRead && (
                                  <span className="inline-block ms-2 h-2 w-2 rounded-full bg-primary align-middle" />
                                )}
                              </h3>
                              <div
                                className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {!n.isRead && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    title={t("notif_mark_read")}
                                    onClick={() => markAsRead(n._id)}
                                  >
                                    <Check className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                  title={t("delete")}
                                  onClick={() => deleteNotification(n._id)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>

                            {/* Full message — this page never truncates */}
                            <p className="text-sm text-muted-foreground mt-1 leading-relaxed whitespace-pre-wrap">
                              {n.message}
                            </p>

                            <div className="flex items-center gap-3 mt-2 flex-wrap">
                              {n.metadata?.fromUser?.displayName && (
                                <span className="text-xs text-muted-foreground/80">
                                  {t("notif_from", { name: n.metadata.fromUser.displayName })}
                                </span>
                              )}
                              <span className="text-xs text-muted-foreground/70">
                                {new Date(n.createdAt).toLocaleString(i18n.language || "en", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                              {target && (
                                <span className="inline-flex items-center gap-1 text-xs text-primary/80 group-hover:text-primary transition-colors ms-auto">
                                  {t("notif_open")}
                                  <ArrowRight className="w-3 h-3 rtl:rotate-180" />
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}

            {hasMore && (
              <div className="text-center pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-full"
                  disabled={isLoading}
                  onClick={() =>
                    fetchNotifications(filter === "unread", notifications.length, 50)
                  }
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 me-2 animate-spin" />
                  ) : null}
                  {t("load_more")}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
